import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { FileText, CheckCircle, AlertTriangle, Users, CreditCard, BookOpen } from 'lucide-react';

export default function TermsOfService() {
  return (
    <div className="min-h-screen pt-20 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Header */}
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold">Terms of Service</h1>
            <p className="text-muted-foreground">Last updated: January 11, 2026</p>
          </div>

          <Card>
            <CardContent className="p-8 prose prose-gray dark:prose-invert max-w-none">
              <section className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <CheckCircle className="w-6 h-6 text-blue-500" />
                    1. Acceptance of Terms
                  </h2>
                  <p className="text-muted-foreground mt-4">
                    By accessing or using the Visual Institute of Arts ("VIA") platform, website, or services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
                  </p>
                  <p className="text-muted-foreground mt-4">
                    VIA reserves the right to modify these terms at any time. Continued use of the platform after changes constitutes acceptance of the modified terms.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <Users className="w-6 h-6 text-blue-500" />
                    2. User Accounts
                  </h2>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-4">
                    <li>You must provide accurate and complete information when creating an account</li>
                    <li>You are responsible for maintaining the confidentiality of your login credentials</li>
                    <li>You must be at least 16 years old to create an account (parental consent required for minors under 18)</li>
                    <li>One account per person; sharing accounts is prohibited</li>
                    <li>You are responsible for all activities under your account</li>
                    <li>Notify us immediately of any unauthorized access to your account</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <BookOpen className="w-6 h-6 text-blue-500" />
                    3. Educational Services
                  </h2>
                  <h3 className="text-xl font-semibold mt-4">3.1 Course Access</h3>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-2">
                    <li><strong>Self-Learning:</strong> Access to course materials for 12 months from enrollment</li>
                    <li><strong>Instructor-Led:</strong> Access during the course duration plus 6 months for revision</li>
                    <li>Course content may be updated periodically to reflect industry changes</li>
                  </ul>

                  <h3 className="text-xl font-semibold mt-4">3.2 Certificates</h3>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-2">
                    <li>Certificates are issued upon successful completion of course requirements</li>
                    <li>Completion requires finishing all modules and passing assessments with minimum 60% score</li>
                    <li>Certificates are verifiable through our platform</li>
                  </ul>

                  <h3 className="text-xl font-semibold mt-4">3.3 Mentorship</h3>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-2">
                    <li>Instructor-Led programs include assigned mentors</li>
                    <li>Mentor availability is subject to scheduling</li>
                    <li>Mentor assignments may change due to operational requirements</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <CreditCard className="w-6 h-6 text-blue-500" />
                    4. Payments & Pricing
                  </h2>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-4">
                    <li>All prices are in Indian Rupees (INR) and inclusive of applicable taxes</li>
                    <li>Prices are subject to change without prior notice (enrolled students retain their locked-in price)</li>
                    <li>Payment must be completed to access course content</li>
                    <li>We accept payments through Razorpay (UPI, Cards, Net Banking, Wallets)</li>
                    <li>Promotional discounts and coupons are subject to specific terms and validity periods</li>
                    <li>EMI options may be available through payment partners</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold">5. Intellectual Property</h2>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-4">
                    <li>All course content, materials, videos, and resources are owned by VIA or its licensors</li>
                    <li>You are granted a limited, non-exclusive, non-transferable license to access course materials for personal educational use</li>
                    <li>Copying, distributing, modifying, or sharing course content is strictly prohibited</li>
                    <li>Screen recording or downloading video content is prohibited</li>
                    <li>Your submitted assignments and projects remain your intellectual property, but you grant VIA a license to use them for portfolio showcases with your consent</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <AlertTriangle className="w-6 h-6 text-amber-500" />
                    6. Prohibited Conduct
                  </h2>
                  <p className="text-muted-foreground mt-4">You agree NOT to:</p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-2">
                    <li>Share your account credentials with others</li>
                    <li>Copy, reproduce, or distribute course content</li>
                    <li>Use automated systems to access the platform</li>
                    <li>Harass, abuse, or harm other users, students, or mentors</li>
                    <li>Submit plagiarized work or cheat on assessments</li>
                    <li>Misrepresent your identity or qualifications</li>
                    <li>Attempt to gain unauthorized access to any part of our systems</li>
                    <li>Use the platform for any illegal purpose</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold">7. Termination</h2>
                  <p className="text-muted-foreground mt-4">
                    VIA may suspend or terminate your account for:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-2">
                    <li>Violation of these Terms of Service</li>
                    <li>Fraudulent activity or misrepresentation</li>
                    <li>Non-payment of fees</li>
                    <li>Conduct harmful to other users or VIA's reputation</li>
                  </ul>
                  <p className="text-muted-foreground mt-4">
                    Upon termination, your access to courses and materials will be revoked. No refund will be provided for termination due to violation of terms.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold">8. Disclaimer of Warranties</h2>
                  <p className="text-muted-foreground mt-4">
                    Our services are provided "as is" without warranties of any kind. While we strive to provide high-quality education, we do not guarantee:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-2">
                    <li>Specific career outcomes or job placements</li>
                    <li>Uninterrupted or error-free service</li>
                    <li>That courses will meet all individual learning needs</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold">9. Limitation of Liability</h2>
                  <p className="text-muted-foreground mt-4">
                    To the maximum extent permitted by law, VIA shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our services. Our total liability shall not exceed the amount paid by you for the specific course in question.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold">10. Dispute Resolution</h2>
                  <p className="text-muted-foreground mt-4">
                    Any disputes arising from these terms shall be:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-2">
                    <li>First attempted to be resolved through direct communication</li>
                    <li>Subject to mediation if direct resolution fails</li>
                    <li>Governed by the laws of India</li>
                    <li>Subject to the exclusive jurisdiction of courts in Mumbai, Maharashtra</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold">11. Contact Information</h2>
                  <p className="text-muted-foreground mt-4">
                    For questions about these terms, contact us at:
                  </p>
                  <p className="text-muted-foreground mt-2">
                    <strong>Email:</strong> legal@visualinstituteofarts.com<br />
                    <strong>Address:</strong> Visual Institute of Arts, Mumbai, Maharashtra, India
                  </p>
                </div>

                <div className="border-t pt-6 mt-8">
                  <p className="text-sm text-muted-foreground">
                    These Terms of Service constitute the entire agreement between you and Visual Institute of Arts regarding the use of our services.
                  </p>
                </div>
              </section>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
